import React, { useState } from "react";
import { X, Copy, Check, Share2, MessageCircle, Mail, Link as LinkIcon, QrCode } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShareModal() {
  const { shareOpen, setShareOpen, artist } = useApp();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  if (!shareOpen) return null;

  const code = artist.shareCode || "";
  const link = `${window.location.origin}/?join=${code}`;
  const qrUrl = `${BACKEND_URL}/api/share/qr?data=${encodeURIComponent(link)}`;
  const message = `🎧 ${artist.name} is now on S71 — my official music portal. Own my singles, watch exclusive videos & join the membership. Check it out:`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast({ title: "Link copied", description: "Share it with your fans anywhere." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Couldn't copy", description: "Please copy the link manually.", variant: "destructive" });
    }
  };

  const enc = encodeURIComponent;
  const shareLinks = [
    { label: "WhatsApp", color: "#25D366", href: `https://wa.me/?text=${enc(message + " " + link)}` },
    { label: "X", color: "#1d1d1f", href: `https://twitter.com/intent/tweet?text=${enc(message)}&url=${enc(link)}` },
    { label: "Facebook", color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}` },
    { label: "Email", color: "#5b6470", href: `mailto:?subject=${enc(`${artist.name} on S71`)}&body=${enc(message + "\n\n" + link)}` },
  ];

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `${artist.name} on S71`, text: message, url: link });
      } catch (e) { /* user dismissed the native share sheet */ console.debug("native share cancelled", e); }
    } else {
      copy();
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShareOpen(false)} />
      <div className="relative z-10 w-full max-w-[420px] rounded-t-3xl sm:rounded-3xl bg-[#101012] border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-5 h-14 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Share2 size={16} className="text-[#8b5cf6]" />
            <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase">Promote Your Portal</h3>
          </div>
          <button onClick={() => setShareOpen(false)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <p className="text-[13px] text-white/55 leading-relaxed mb-4">
            This is your <span className="text-white">exclusive link</span>. Share it so fans discover your
            official {artist.name} portal — buy your music, watch videos & join the membership.
          </p>

          {/* Link box */}
          <div className="flex items-center gap-2 px-3 h-12 rounded-xl bg-white/[0.05] border border-white/10">
            <LinkIcon size={15} className="text-white/40 shrink-0" />
            <span className="flex-1 text-[13px] text-white/80 truncate">{link}</span>
            <button
              onClick={copy}
              className="shrink-0 flex items-center gap-1 px-3 h-8 rounded-lg bg-white text-black text-[12px] font-semibold hover:bg-white/85"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>

          {/* Social buttons */}
          <p className="text-[11px] uppercase tracking-wide text-white/40 mt-5 mb-2">Share via</p>
          <div className="grid grid-cols-4 gap-2">
            {shareLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition-colors"
              >
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: s.color }}>
                  {s.label === "WhatsApp" && <MessageCircle size={16} className="text-white" />}
                  {s.label === "X" && <span className="text-white font-bold text-[15px]">𝕏</span>}
                  {s.label === "Facebook" && <span className="text-white font-bold text-[15px]">f</span>}
                  {s.label === "Email" && <Mail size={16} className="text-white" />}
                </span>
                <span className="text-[10px] text-white/55">{s.label}</span>
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button onClick={nativeShare} className="flex-1 h-11 rounded-xl bg-[#8b5cf6] text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#7c4ef0]">
              <Share2 size={15} /> Share…
            </button>
            <button onClick={() => setShowQr((v) => !v)} className="h-11 px-4 rounded-xl bg-white/5 text-white/70 text-[13px] flex items-center gap-2 hover:bg-white/10">
              <QrCode size={15} /> QR
            </button>
          </div>

          {showQr && (
            <div className="mt-4 flex flex-col items-center gap-2 p-4 rounded-xl bg-white">
              <img src={qrUrl} alt="Portal QR code" className="w-40 h-40" />
              <span className="text-[11px] text-black/50">Scan to open {artist.name}'s portal</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
